import { FC, useEffect, useState } from 'react'
import { useTheme } from '@liveconnect/components'
import { useTranslation } from 'react-i18next'

import { Main } from '../../../components/Main'
import ButtonsMenu from './Menu'
import ButtonsContainer from './Container'
import ButtonsConfiguration from './Configuration'
import TenantDesignHeader from '../Header'
import FormActions from '../../../components/FormActions'

import useTenantDesign from '../../../core/tenantDesign/useTenantDesign'
import useUi from '../../../core/ui/useUi'

import { Color } from '../types'

import './styles.scss'

const ButtonsDesign: FC = () => {
  const labelledby = 'buttons-design'
  const { t } = useTranslation()
  const { getDesigns } = useTenantDesign()
  const { changeThemeVars } = useTheme()
  const { showConfirmation } = useUi()

  const [colors, setColorsList] = useState<Record<string, string>>()
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false)
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false)
  const [isFormCancelled, setIsFormCancelled] = useState<boolean>(false)
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false)

  const getComponentDesign = async () => {
    const response = await getDesigns('buttons')
    const responseRecord = arrayToRecord(response ?? [])
    setColorsList(responseRecord)
    changeThemeVars(responseRecord)
  }

  const arrayToRecord = (array: Color[]): Record<string, string> =>
    array.reduce(
      (accumulator, currentValue) => (
        (accumulator[currentValue['key']] = currentValue['value']), accumulator
      ),
      {}
    )

  useEffect(() => {
    showCancelModal &&
      showConfirmation({
        className: 'buttons-design__modal',
        title: t('tenantDesign.modal.cancel.title'),
        subtitle: t('tenantDesign.modal.cancel.body'),
        iconName: 'report_problem',
        confirmText: t('common.cancel'),
        cancelText: t('common.close'),
        onConfirm: () => {
          setIsFormCancelled(true)
          setShowCancelModal(false)
        },
        onCancel: () => {
          setShowCancelModal(false)
        },
      })
  }, [showCancelModal])

  useEffect(() => {
    getComponentDesign()
  }, [])

  return (
    <Main ariaLabelledby={labelledby} className="buttons-design">
      <div className="buttons-design sticky-container">
        <TenantDesignHeader labelledby={labelledby} />
        <div className="buttons-design__body row">
          <div className="buttons-design__body__menu col-2">
            <ButtonsMenu />
          </div>
          <div className="buttons-design__body__container col-8">
            <ButtonsContainer />
          </div>
          <div className="buttons-design__body__configuration col-2">
            {colors && (
              <ButtonsConfiguration
                colors={colors}
                triggerCancel={() => isFormCancelled}
                formCancelled={() => setIsFormCancelled(false)}
                triggerSubmit={() => isFormSubmitted}
                formSubmitted={() => setIsFormSubmitted(false)}
                formDirty={(isDirty) => setIsFormDirty(isDirty)}
              />
            )}
          </div>
        </div>
      </div>
      <FormActions>
        <button
          className="buttons-design__footer__cancel btn btn-outline-primary"
          onClick={() => isFormDirty && setShowCancelModal(true)}
        >
          {t('common.cancel')}
        </button>
        <button
          className="buttons-design__footer__save btn btn-primary"
          onClick={() => {
            setIsFormSubmitted(true)
          }}
          disabled={!isFormDirty}
        >
          {t('common.save')}
        </button>
      </FormActions>
    </Main>
  )
}

export default ButtonsDesign
